import { CellWithTooltip } from "@/components/data-table/CellWithTooltip";
import { ColumnHeader } from "@/components/data-table/ColumnHeader";
import { Badge } from "@/components/ui/badge";
import { SubscriptionOfferStatus } from "@/gql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import SubscriptionOfferActions from "../components/SubscriptionOffers/SubscriptionOfferActions";
import { SubscriptionOfferTable } from "../types/subscriptions.type";
import { sortByPrice } from "../utils/sortByPrice";

type SubscriptionOfferColumn = ColumnDef<SubscriptionOfferTable>;

const imageColumn: SubscriptionOfferColumn = {
  accessorKey: "appImage",
  cell: ({ row }) => (
    <div className="h-full w-fit">
      <img
        className="object-cover h-12 rounded"
        src={row.getValue("appImage") || "/app-placeholder-picture.png"}
        alt={row.getValue("appImage") || ""}
      />
    </div>
  ),
  header: undefined,
  enableHiding: false
};

const nameColumn: SubscriptionOfferColumn = {
  accessorKey: "appName",
  cell: ({ row }) => <CellWithTooltip cellValue={row.getValue("appName")} />,
  header: ({ column }) => <ColumnHeader column={column} title="App" />,
  enableHiding: false
};

const recepientColumn: SubscriptionOfferColumn = {
  accessorKey: "email",
  cell: ({ row }) => <CellWithTooltip cellValue={row.getValue("email")} />,
  header: ({ column }) => (
    <ColumnHeader column={column} title="Recipient email" />
  ),
  enableHiding: false
};

const statusColumn: SubscriptionOfferColumn = {
  accessorKey: "status",
  cell: ({ row }) => {
    const status: SubscriptionOfferStatus = row.getValue("status");
    return (
      <Badge variant="secondary" className="font-normal">
        <div
          className={cn(
            "w-2 h-2 rounded-full mr-2",
            {
              [SubscriptionOfferStatus.Accepted]: "bg-brand-primary",
              [SubscriptionOfferStatus.Pending]: "bg-brand-warning",
              [SubscriptionOfferStatus.Rejected]: "bg-brand-error",
              [SubscriptionOfferStatus.Withdrawn]: "bg-brand-gray-200",
              [SubscriptionOfferStatus.Expired]: "bg-brand-gray-200"
            }[status]
          )}
        />
        <div
          className="lowercase first-letter:capitalize dark:text-brand-gray-200"
          data-testid="subscriptionOfferStatus"
        >
          {status}
        </div>
      </Badge>
    );
  },
  header: ({ column }) => <ColumnHeader column={column} title="Status" />,
  enableHiding: false
};

const createdColumn: SubscriptionOfferColumn = {
  accessorKey: "createdAt",
  cell: ({ row }) => <span>{format(row.getValue("createdAt"), "PP")}</span>,
  header: ({ column }) => <ColumnHeader column={column} title="Sent" />,
  enableHiding: false
};

const priceColumn: SubscriptionOfferColumn = {
  accessorKey: "monthlyPriceUSD",
  cell: ({ row }) => {
    const price: SubscriptionOfferTable["monthlyPriceUSD"] =
      row.getValue("monthlyPriceUSD");
    if (!price) return <span data-testid="priceCell">Free</span>;
    return <span data-testid="priceCell">${price}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Price" />,
  sortingFn: sortByPrice,
  enableHiding: false
};

const trialColumn: SubscriptionOfferColumn = {
  accessorKey: "trialDays",
  cell: ({ row }) => {
    const trialDays: SubscriptionOfferTable["trialDays"] =
      row.getValue("trialDays");
    if (!trialDays) return null;
    return <span>{trialDays} days</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Trial days" />,
  enableHiding: false
};

const referenceColumn: SubscriptionOfferColumn = {
  accessorKey: "reference",
  cell: ({ row }) => (
    <CellWithTooltip cellValue={row.getValue("reference") || ""} />
  ),
  header: ({ column }) => <ColumnHeader column={column} title="Reference" />,
  enableHiding: true
};

const expirationColumn: SubscriptionOfferColumn = {
  accessorKey: "expiresAt",
  cell: ({ row }) => {
    const expiresAt: SubscriptionOfferTable["expiresAt"] =
      row.getValue("expiresAt");
    if (!expiresAt) return null;
    return <span>{format(expiresAt, "PP")}</span>;
  },
  header: ({ column }) => <ColumnHeader column={column} title="Expiration" />,
  enableHiding: true
};

const actionsColumn: SubscriptionOfferColumn = {
  id: "action",
  cell: ({ row }) => <SubscriptionOfferActions offer={row.original} />,
  header: undefined
};

export const subscriptionOfferColumns: SubscriptionOfferColumn[] = [
  imageColumn,
  nameColumn,
  recepientColumn,
  statusColumn,
  referenceColumn,
  createdColumn,
  priceColumn,
  trialColumn,
  expirationColumn,
  actionsColumn
];
